/**
 * Which work order groups of the sample register a person left open or closed
 * (D323), remembered in THIS browser only.
 *
 * It is a convenience, never a record: nothing is sent to the server, and a
 * private window, blocked site data or a full storage simply forgets it — the
 * register then opens its first group, as it would on a first visit. The key
 * carries the laboratory and the person, so two people sharing a browser keep
 * their own layout. Only the most recent `MAX_REMEMBERED` choices are kept, so
 * years of work orders cannot grow it without bound.
 */

const MAX_REMEMBERED = 200;

export type OpenGroups = Readonly<Record<string, boolean>>;

export function readOpenGroups(storageKey: string): OpenGroups {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const result: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'boolean') result[key] = value;
    }
    return result;
  } catch {
    return {};
  }
}

/**
 * Records one group's choice and returns the whole new set. The newest choice
 * is written last, so trimming drops the oldest.
 */
export function rememberOpenGroup(storageKey: string, groupKey: string, isOpen: boolean): OpenGroups {
  const current = { ...readOpenGroups(storageKey) };
  delete current[groupKey];
  current[groupKey] = isOpen;
  const keys = Object.keys(current);
  for (const stale of keys.slice(0, Math.max(0, keys.length - MAX_REMEMBERED))) {
    delete current[stale];
  }
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(current));
  } catch {
    // Storage unavailable: the choice holds for this visit only.
  }
  return current;
}
